/**
 * Pagos offline (dentro de Licencias): registro y visibilidad de pagos fuera de
 * la pasarela (transferencia/depósito/Yape/Plin/efectivo). El registro CON
 * COMPROBANTE activa la licencia (paridad con el webhook, regla de oro M10); la
 * anulación puede revertir el acceso si no queda otra evidencia vigente. Da al
 * negocio la foto de cuánto entra por cada método.
 */
import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { adminApi, billingApi } from "../../api/endpoints.js";
import { useQuery, mensajeDeError } from "../../hooks/useQuery.js";
import type {
  EstadoPagoOffline,
  MetodoPagoOffline,
  PagoOffline,
  Product,
  UsuarioAdmin,
} from "../../api/types.js";
import { METODOS_PAGO_OFFLINE } from "../../api/types.js";
import { Async, ConfirmDialog, fmtFechaCorta } from "./ui.js";
import s from "./admin.module.css";

const METODO_LABEL: Record<MetodoPagoOffline, string> = {
  transferencia: "Transferencia",
  deposito: "Depósito",
  yape: "Yape",
  plin: "Plin",
  efectivo: "Efectivo",
  otro: "Otro",
};

function EstadoPagoBadge({ estado }: { estado: EstadoPagoOffline }) {
  return estado === "registrado" ? (
    <span className={`${s.badge} ${s.badgeGreen}`}>Registrado</span>
  ) : (
    <span className={`${s.badge} ${s.badgeNeutral}`}>Anulado</span>
  );
}

export function AdminPagosOffline() {
  const [metodo, setMetodo] = useState<"" | MetodoPagoOffline>("");
  const [estado, setEstado] = useState<"" | EstadoPagoOffline>("");
  const [desde, setDesde] = useState("");
  const [hasta, setHasta] = useState("");
  const [mostrarForm, setMostrarForm] = useState(false);
  const [anular, setAnular] = useState<PagoOffline | null>(null);

  const q = useQuery(
    () =>
      adminApi.pagosOffline({
        ...(metodo ? { metodo } : {}),
        ...(estado ? { estado } : {}),
        ...(desde ? { desde } : {}),
        ...(hasta ? { hasta } : {}),
        limit: 200,
      }),
    [metodo, estado, desde, hasta],
  );

  const totales = useMemo(() => {
    const map = new Map<string, { moneda: string; total: number }>();
    for (const p of q.data?.items ?? []) {
      if (p.estado !== "registrado") continue;
      const k = `${p.metodo}·${p.moneda}`;
      const prev = map.get(k) ?? { moneda: p.moneda, total: 0 };
      map.set(k, { moneda: p.moneda, total: prev.total + p.monto });
    }
    return [...map.entries()].map(([k, v]) => ({ metodo: k.split("·")[0], ...v }));
  }, [q.data]);

  return (
    <>
      <VolverLicencias />
      <div className={s.sectionTitle}>Pagos offline</div>

      <div className={s.toolbar}>
        <select className={s.select} value={metodo} onChange={(e) => setMetodo(e.target.value as "" | MetodoPagoOffline)}>
          <option value="">Todo método</option>
          {METODOS_PAGO_OFFLINE.map((m) => (
            <option key={m} value={m}>
              {METODO_LABEL[m]}
            </option>
          ))}
        </select>
        <select className={s.select} value={estado} onChange={(e) => setEstado(e.target.value as "" | EstadoPagoOffline)}>
          <option value="">Todo estado</option>
          <option value="registrado">Registrado</option>
          <option value="anulado">Anulado</option>
        </select>
        <label className={s.count}>
          Desde{" "}
          <input className={s.search} type="date" value={desde} onChange={(e) => setDesde(e.target.value)} />
        </label>
        <label className={s.count}>
          Hasta{" "}
          <input className={s.search} type="date" value={hasta} onChange={(e) => setHasta(e.target.value)} />
        </label>
        <span className={s.headerSpacer} />
        <button className={`${s.btn} ${s.btnPrimary}`} onClick={() => setMostrarForm((v) => !v)}>
          {mostrarForm ? "Cerrar" : "Registrar pago offline"}
        </button>
      </div>

      {mostrarForm ? (
        <FormRegistro
          onHecho={() => {
            setMostrarForm(false);
            q.reload();
          }}
        />
      ) : null}

      {totales.length ? (
        <div className={s.toolbar} style={{ flexWrap: "wrap", gap: 8 }}>
          {totales.map((t) => (
            <span key={`${t.metodo}${t.moneda}`} className={`${s.badge} ${s.badgeBlue}`}>
              {METODO_LABEL[t.metodo as MetodoPagoOffline]}: {t.total.toFixed(2)} {t.moneda}
            </span>
          ))}
        </div>
      ) : null}

      <div className={s.panel}>
        <Async q={q}>
          {(data) => (
            <div className={s.tableWrap}>
              <table className={s.table}>
                <thead>
                  <tr>
                    <th>Fecha pago</th>
                    <th>Terapeuta</th>
                    <th>Producto</th>
                    <th>Monto</th>
                    <th>Método</th>
                    <th>Referencia</th>
                    <th>Estado</th>
                    <th>Registró</th>
                    <th>Comprobante</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {data.items.map((p) => (
                    <tr key={p.id}>
                      <td className={s.muted}>{p.fechaPago}</td>
                      <td>
                        {p.terapeutaNombre ?? <span className={s.mono}>{p.terapeutaId.slice(0, 8)}</span>}
                        {p.terapeutaEmail ? <div className={s.muted}>{p.terapeutaEmail}</div> : null}
                      </td>
                      <td className={s.muted}>{p.productoNombre ?? p.productoCodigo ?? "—"}</td>
                      <td>
                        {p.monto.toFixed(2)} {p.moneda}
                        {p.motivoDiferencia ? (
                          <div className={s.muted} title={p.motivoDiferencia}>
                            ⚠ dif.
                          </div>
                        ) : null}
                      </td>
                      <td>{METODO_LABEL[p.metodo]}</td>
                      <td className={s.mono}>{p.referenciaOperacion ?? "—"}</td>
                      <td>
                        <EstadoPagoBadge estado={p.estado} />
                      </td>
                      <td className={s.muted}>{p.registradoPorNombre ?? "—"}</td>
                      <td>
                        {p.comprobanteUrl ? (
                          <a className={s.btn} href={p.comprobanteUrl} target="_blank" rel="noreferrer">
                            Ver
                          </a>
                        ) : (
                          "—"
                        )}
                      </td>
                      <td>
                        {p.estado === "registrado" ? (
                          <button className={`${s.btn} ${s.btnDanger}`} onClick={() => setAnular(p)}>
                            Anular
                          </button>
                        ) : p.anuladoMotivo ? (
                          <span className={s.muted} title={p.anuladoMotivo}>
                            anulado
                          </span>
                        ) : null}
                      </td>
                    </tr>
                  ))}
                  {data.items.length === 0 ? (
                    <tr>
                      <td colSpan={10} className={s.empty}>
                        Sin pagos offline con esos filtros.
                      </td>
                    </tr>
                  ) : null}
                </tbody>
              </table>
            </div>
          )}
        </Async>
      </div>

      {anular ? (
        <AnularDialog pago={anular} onCerrar={() => setAnular(null)} onHecho={() => { setAnular(null); q.reload(); }} />
      ) : null}
    </>
  );
}

function AnularDialog({
  pago,
  onCerrar,
  onHecho,
}: {
  pago: PagoOffline;
  onCerrar: () => void;
  onHecho: () => void;
}) {
  const [motivo, setMotivo] = useState("");
  const [ocupado, setOcupado] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function ejecutar() {
    if (!motivo.trim()) {
      setError("El motivo es obligatorio.");
      return;
    }
    setOcupado(true);
    setError(null);
    try {
      await adminApi.anularPagoOffline(pago.id, motivo.trim());
      onHecho();
    } catch (e) {
      setError(mensajeDeError(e));
    } finally {
      setOcupado(false);
    }
  }

  return (
    <ConfirmDialog
      titulo="Anular pago offline"
      cuerpo={
        <>
          <p>
            Anular el pago de {pago.monto.toFixed(2)} {pago.moneda} de{" "}
            {pago.terapeutaNombre ?? "el terapeuta"}. Si no queda otra evidencia de pago vigente, se
            revierte el acceso. El registro y el comprobante se conservan.
          </p>
          {error ? <div className={`${s.banner} ${s.bannerError}`}>{error}</div> : null}
          <textarea
            className={s.search}
            style={{ width: "100%", minHeight: 64, marginTop: 8 }}
            placeholder="Motivo (obligatorio): registrado por error, transferencia rebotada…"
            value={motivo}
            onChange={(e) => setMotivo(e.target.value)}
          />
        </>
      }
      confirmar="Anular"
      peligro
      ocupado={ocupado}
      onConfirm={() => void ejecutar()}
      onCancel={onCerrar}
    />
  );
}

function FormRegistro({ onHecho }: { onHecho: () => void }) {
  const usuariosQ = useQuery(() => adminApi.usuarios(), []);
  const productosQ = useQuery(() => billingApi.productos(), []);

  const [terapeutaId, setTerapeutaId] = useState("");
  const [productId, setProductId] = useState("");
  const [monto, setMonto] = useState("");
  const [moneda, setMoneda] = useState("");
  const [metodo, setMetodo] = useState<MetodoPagoOffline>("transferencia");
  const [referencia, setReferencia] = useState("");
  const [fechaPago, setFechaPago] = useState("");
  const [motivoDiferencia, setMotivoDiferencia] = useState("");
  const [archivo, setArchivo] = useState<File | null>(null);
  const [ocupado, setOcupado] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Compradores: terapeutas (suscripciones) y padres (paquetes).
  const compradores = (usuariosQ.data ?? []).filter(
    (u: UsuarioAdmin) => u.rol === "terapeuta" || u.rol === "padre",
  );
  const productos = productosQ.data ?? [];
  const producto = productos.find((p: Product) => p.id === productId) ?? null;
  const difiere = producto !== null && (Number(monto) !== producto.precio || moneda !== producto.moneda);

  function elegirProducto(id: string) {
    setProductId(id);
    const p = productos.find((x: Product) => x.id === id);
    if (p) {
      setMonto(String(p.precio));
      setMoneda(p.moneda);
    }
  }

  async function enviar() {
    setError(null);
    if (!terapeutaId) return setError("Elige un comprador (terapeuta o padre).");
    if (!productId) return setError("Elige un plan o paquete.");
    if (!fechaPago) return setError("Indica la fecha del comprobante.");
    if (!archivo) return setError("El comprobante es obligatorio.");
    if (difiere && !motivoDiferencia.trim()) {
      return setError("El monto difiere del precio del plan: indica el motivo.");
    }
    const form = new FormData();
    form.append("terapeutaId", terapeutaId);
    form.append("productId", productId);
    form.append("monto", monto);
    form.append("moneda", moneda);
    form.append("metodo", metodo);
    if (referencia.trim()) form.append("referenciaOperacion", referencia.trim());
    form.append("fechaPago", fechaPago);
    if (difiere && motivoDiferencia.trim()) form.append("motivoDiferencia", motivoDiferencia.trim());
    form.append("file", archivo);

    setOcupado(true);
    try {
      await adminApi.registrarPagoOffline(form);
      onHecho();
    } catch (e) {
      setError(mensajeDeError(e));
    } finally {
      setOcupado(false);
    }
  }

  return (
    <div className={`${s.panel} ${s.panelPad}`} style={{ marginBottom: 12 }}>
      <div className={s.dt}>Registrar pago offline</div>
      {error ? <div className={`${s.banner} ${s.bannerError}`}>{error}</div> : null}
      <div className={s.dl} style={{ marginTop: 8 }}>
        <label>
          <div className={s.dt}>Comprador (terapeuta o padre)</div>
          <select className={s.select} value={terapeutaId} onChange={(e) => setTerapeutaId(e.target.value)}>
            <option value="">— elegir —</option>
            {compradores.map((u: UsuarioAdmin) => (
              <option key={u.id} value={u.id}>
                [{u.rol}] {u.nombre} · {u.email}
              </option>
            ))}
          </select>
        </label>
        <label>
          <div className={s.dt}>Plan / paquete</div>
          <select className={s.select} value={productId} onChange={(e) => elegirProducto(e.target.value)}>
            <option value="">— elegir —</option>
            {productos.map((p: Product) => (
              <option key={p.id} value={p.id}>
                [{p.audiencia}] {p.nombre} · {p.precio} {p.moneda}
                {p.modalidad === "suscripcion" ? ` (${p.intervalo})` : " (paquete)"}
              </option>
            ))}
          </select>
        </label>
        <label>
          <div className={s.dt}>Monto</div>
          <input className={s.search} type="number" step="0.01" value={monto} onChange={(e) => setMonto(e.target.value)} />
        </label>
        <label>
          <div className={s.dt}>Moneda</div>
          <input className={s.search} value={moneda} onChange={(e) => setMoneda(e.target.value)} />
        </label>
        <label>
          <div className={s.dt}>Método</div>
          <select className={s.select} value={metodo} onChange={(e) => setMetodo(e.target.value as MetodoPagoOffline)}>
            {METODOS_PAGO_OFFLINE.map((m) => (
              <option key={m} value={m}>
                {METODO_LABEL[m]}
              </option>
            ))}
          </select>
        </label>
        <label>
          <div className={s.dt}>Nº de operación</div>
          <input className={s.search} value={referencia} onChange={(e) => setReferencia(e.target.value)} />
        </label>
        <label>
          <div className={s.dt}>Fecha del comprobante</div>
          <input className={s.search} type="date" value={fechaPago} onChange={(e) => setFechaPago(e.target.value)} />
        </label>
        <label>
          <div className={s.dt}>Comprobante (png/jpg/webp/pdf, máx 5MB)</div>
          <input
            className={s.search}
            type="file"
            accept="image/png,image/jpeg,image/webp,application/pdf"
            onChange={(e) => setArchivo(e.target.files?.[0] ?? null)}
          />
        </label>
      </div>
      {difiere ? (
        <div style={{ marginTop: 8 }}>
          <div className={s.dt}>Motivo de la diferencia (monto ≠ precio del plan)</div>
          <input
            className={s.search}
            style={{ width: "100%" }}
            placeholder="Ej. descuento acordado"
            value={motivoDiferencia}
            onChange={(e) => setMotivoDiferencia(e.target.value)}
          />
        </div>
      ) : null}
      <div className={s.btnRow} style={{ marginTop: 12 }}>
        <button className={`${s.btn} ${s.btnPrimary}`} disabled={ocupado} onClick={() => void enviar()}>
          {ocupado ? "Registrando…" : "Registrar y activar"}
        </button>
      </div>
    </div>
  );
}

/** Enlace de cabecera para volver desde la vista de licencias. */
export function VolverLicencias() {
  return (
    <Link to="/admin/creditos" className={s.backLink}>
      ‹ Volver a Créditos
    </Link>
  );
}
